import { AnalysisRun } from "./analysis-run.service";
import { officeCall } from "../utils/analysis-async";
import { asyncCallback } from "../utils/analysis-async";
import { Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import {
  APIResponse,
  CacheNamesEnum,
  Constants,
  DataDTO,
  DomainDTO,
  EtagCacheModel,
  EtagEntitiesEnum,
  EvFlow,
  HttpErrorsEnum,
  PointsDTO,
  TokenTypesEnum,
  UserDTO
} from "../models/shared.model";
import { BehaviorSubject, of, Subject, timeout, TimeoutError } from "rxjs";
import { StepResult } from "../models/step-result";
import { LogService } from "./log.service";
import { environment } from "../../environments/environment";
import { OfficeLoginService } from "./officeLogin.service";
import moment from "moment/moment";
import { jwtDecode } from "jwt-decode";
import { catchError } from "rxjs/operators";
import { SettingResponse, SettingsEnum } from "../models/settings.model";
import { StringUtils } from "../utils/string.utils";
import { ConfigService } from "./config.service";
import { SettingsService } from "./settings.service";

@Injectable()
export class SharedService {
  private baseUrl: string;
  private cacheRefresh: ReturnType<typeof setInterval>;
  private accessToken: string;
  private idToken: string;

  public messageReceived: Subject<StepResult> = new Subject();
  public itemChanged = new BehaviorSubject<boolean>(false);

  constructor(
    private officeLoginService: OfficeLoginService,
    private http: HttpClient,
    private log: LogService,
    private configService: ConfigService,
    private settingService: SettingsService
  ) {
    this.baseUrl = environment.backendUrl;

  }

  async getOwnerOfSharedFolder(run?: AnalysisRun): Promise<string | null> {
    run?.assertActive();
    const item = run ? run.item : Office.context.mailbox.item;
    if (!item?.getSharedPropertiesAsync) return null;
    try {
      const properties = await officeCall<Office.SharedProperties>(callback => item.getSharedPropertiesAsync(callback),
        'Shared folder properties', run);
      return properties.owner;
    } catch (error) {
      run?.assertActive();
      // Office reports failure for an ordinary (non-shared) mailbox.
      if (error?.code === 'timeout') throw error;
      return null;
    }
  }

  async initUpdateCacheTask() {
    // Set the cache then update it every 1 hour
    return asyncCallback<any>(async (resolve, reject) => {
      const maxAttempts = 3;
      let attempts = 0;
      // If token expired, remove it from cache and retry calling the APIs to reacquire it.
      // If there is an error, we will retry for 3 times then throw the error
      while (attempts < maxAttempts) {
        try {
          await this.updateCache();
          const hour = 60 * 60 * 1000;
          if (!this.cacheRefresh) this.cacheRefresh = setInterval(() => {
            void this.updateCache().catch(error => this.log.logException(error));
          }, hour);
          resolve(true);
          break;
        } catch (e) {
          console.log("Error in fetching data.");
          console.error(e);
          if (e?.error?.error == HttpErrorsEnum.TOKEN_EXPIRED || e?.error?.error == HttpErrorsEnum.INVALID_TOKEN) {
            console.error("Token Expired. Removing cached token to reacquire it");
            for (let i = 0; i < window.localStorage.length; i++) {
              const key = window.localStorage.key(i);
              if (key.includes("accesstoken")) {
                window.localStorage.setItem(key, null);
              }
            }
            attempts++;

            if (attempts >= maxAttempts) {
              reject(e);
            }
          } else {
            attempts = 3;
            reject(e);
          }
        }
      }
    });
  }

  async getToken(include_alter_inbox_scope: boolean = false): Promise<string> {
    return asyncCallback<string>(async (resolve, reject) => {
      this.officeLoginService.login(include_alter_inbox_scope)
        .then(value => {
          this.accessToken = value;
          this.idToken = this.officeLoginService.idToken;
          window.localStorage.setItem(CacheNamesEnum.TOKEN_TYPE, JSON.stringify(TokenTypesEnum.NAA_TOKEN));

          resolve(value);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async updateCache() {
    await this.getProfile();
    await this.getSettings();
    if (await this.shouldUpdateCache()) {
      await this.getData();

      window.localStorage.setItem(CacheNamesEnum.LAST_UPDATE_DT, JSON.stringify(moment.utc().toISOString()));
    }
  }

  async shouldUpdateCache() {
    // return true;
    const isLoggedInSameAsCachedCheck = await this.isLoggedInUserSameAsCachedAndTokenValid();
    if (!isLoggedInSameAsCachedCheck)
      return true;

    const lastUpdateDT = JSON.parse(window.localStorage.getItem(CacheNamesEnum.LAST_UPDATE_DT));
    const now = moment.utc();

    if (lastUpdateDT != null && moment.utc(lastUpdateDT).isValid()) {
      const lastUpdateMoment = moment.utc(lastUpdateDT);
      return now.diff(lastUpdateMoment, "minutes") >= 60;
    }

    return true;
  }

  /**
   * This method check if the logged-in user is the same as the cached user
   *  if they are the same and the token didn't expire we might not need to update the cache (if the last update was < 60 minutes)
   *  And if they are not the same we update the cache and to check if the user purchased the Add-in
   */
  async isLoggedInUserSameAsCachedAndTokenValid() {
    const accessToken = await this.getToken();

    if(accessToken == null)
      return false;

    let token = "";
    let loggedInUserEmail = "";
    let tokenExpiresIn = 0;

    token = jwtDecode(this.idToken);
    loggedInUserEmail = token["email"];
    tokenExpiresIn = token["exp"];

    const user = JSON.parse(window.localStorage.getItem(CacheNamesEnum.USER)!) as UserDTO;

    if (user != null)
      return user.email == loggedInUserEmail && (moment.unix(tokenExpiresIn).utc().isAfter(moment.utc()));

    return false;
  }

  async getProfile() {
    return new Promise<void>((resolve, reject) => {
      this.http.get(this.baseUrl + "/user/me", {observe: 'response'})
        .pipe(
          timeout(Constants.PROFILE_API_CALL_TIMEOUT)
        )
        .subscribe({
          next: (res: HttpResponse<APIResponse<UserDTO>>) => {
            if(res.status == 304)
              resolve();

            if (res?.body?.ok) {
              window.localStorage.setItem(CacheNamesEnum.USER, JSON.stringify(res.body.data));
              resolve();
            } else if(res?.body?.error)
                reject(res.body.error);
              reject()
          },
          error: (error) => {
            console.error("Failed to fetch User Data");

            if (error instanceof TimeoutError) {
              const res: APIResponse<UserDTO> = {
                ok: false,
                data: null,
                error: HttpErrorsEnum.FAILED_TO_FETCH_DATA
              };

              reject({ error: res });
            }
            reject(error);
          }
        });
    });
  }

  async getData() {
    // Calculate current points obj checksum to retreive the appropriate object from DB
    const checksum = await StringUtils.calculateChecksumPointsObj(this.configService.getFlowPointsAndConditions())

    return new Promise<void>((resolve, reject) => {
      this.http.get(this.baseUrl + "/batch/all", {
        params: {
          checksum: checksum
        },
        observe: "response",
      })
        .pipe(
          timeout(Constants.DEFAULT_API_CALL_TIMEOUT),
          catchError((error) => {
            if (error instanceof TimeoutError) {
              console.error("Cached Data API Call Timed out, Throwing Error");
              const res: APIResponse<DataDTO> = {
                ok: false,
                data: null,
                error: HttpErrorsEnum.FAILED_TO_FETCH_DATA
              };
              return of({ error: res });
            }
            return of(error);
          })
        )
        .subscribe({
          next: (res: HttpResponse<APIResponse<DataDTO>>) => {
            if(res.status == 304)
              resolve();

            const resBody = res.body;

            if (resBody?.ok && resBody?.data && resBody?.data.domains && resBody?.data.points && resBody?.data.free_services && resBody?.data.attachments) {
              this.cacheData(resBody?.data);
              resolve();
            } else
              reject(res.body);
          },
          error: (error) => {
            console.error("Failed to fetch cached data");
            console.error(error);
            this.log.logException(new Error(`Error fetching cached data - ${JSON.stringify(error)}`));
            this.cacheDomains([]);

            reject(error);
          }
        });
    });
  }

  async getSettings() {
    return new Promise<void>((resolve, reject) => {
      this.http.get(this.baseUrl + "/settings", { observe: 'response' })
        .pipe(
          timeout(Constants.DEFAULT_API_CALL_TIMEOUT)
        )
        .subscribe({
          next: (res: HttpResponse<APIResponse<SettingResponse>>) => {
            void (async () => {
            if(res.status == 304) return resolve();

            if (res?.body?.ok) {

              let settings = [...res.body.data.application, ...res.body.data.customer]

              window.localStorage.setItem(CacheNamesEnum.SETTINGS, JSON.stringify(settings));

              if(this.settingService.getSettingValue(SettingsEnum.ENABLE_DELETE_PHISHING_EMAILS) &&
                !this.hasAlterInboxScope()) {
                try {
                  await this.getToken(true);
                } catch (error) {
                  console.warn("Failed to acquire additional scopes for delete email functionality", error);
                }
              }

              resolve();
            } else
              resolve(null);
            })().catch(reject);
          },
          error: (error) => {
            console.warn("Failed to call Settings API");
            console.warn(error);

            resolve(null);
          }
        });
    });
  }

  private hasAlterInboxScope(): boolean {
    if (!this.officeLoginService.tokenScopes) {
      return false;
    }

    const alterInboxScope = environment.apiConfig.alter_inbox_scope[0];
    const scopes = this.officeLoginService.tokenScopes;

    if (typeof scopes === 'string') {
      return scopes.split(' ').some(scope => scope.trim() === alterInboxScope);
    }

    if (Array.isArray(scopes)) {
      return scopes.some(scope => scope === alterInboxScope);
    }

    return false;
  }

  cacheData(data: DataDTO) {
    this.cacheDomains(data.domains);

    window.localStorage.setItem(CacheNamesEnum.PERSONAL_TRUSTED_DOMAINS, JSON.stringify(data.trusted_list));

    window.localStorage.setItem(CacheNamesEnum.POINTS, JSON.stringify(data.points));

    window.localStorage.setItem(CacheNamesEnum.FREE_EMAIL_SERVICE, JSON.stringify(data.free_services));

    if(data.overviews)
      window.localStorage.setItem(CacheNamesEnum.OVERVIEW_SECTIONS, JSON.stringify(data.overviews));

    window.localStorage.setItem(CacheNamesEnum.DANGEROUS_FILE_EXTENSIONS, JSON.stringify(data.attachments));
  }

  getFlowPointsAndConditions(): EvFlow {
    let flowPoints = JSON.parse(window.localStorage.getItem(CacheNamesEnum.POINTS)!) as PointsDTO;

    return flowPoints?.points;
  }

  private cacheDomains(domains: DomainDTO[]) {
    const trusted = domains.filter(value => value.top_level == false && value.safe == true);

    const trustedTLD = domains.filter(value => value.top_level == true && value.safe == true);
    const dangerTLD = domains.filter(value => value.top_level == true && value.safe == false);

    window.localStorage.setItem(CacheNamesEnum.TRUSTED_DOMAINS, JSON.stringify(trusted));
    window.localStorage.setItem(CacheNamesEnum.TRUSTED_TOP_LEVEL_DOMAINS, JSON.stringify(trustedTLD));
    window.localStorage.setItem(CacheNamesEnum.DANGEROUS_TOP_LEVEL_DOMAINS, JSON.stringify(dangerTLD));
  }

  async uploadLogFile(logFileData: string, fileType: string, fileName: string): Promise<any> {
    const formData: FormData = new FormData();

    formData.append("logFile", new Blob([logFileData], { type: fileType }), fileName);

    return new Promise((resolve, reject) => {

      this.http.post(this.baseUrl + `/upload/logs`, formData)
        .subscribe({
          next: (result: APIResponse<string>) => {
            if (!result.ok) {
              console.log("Failed to Upload Log File");
              console.log(result.data);
              resolve(false);
            }
            resolve(true);
          }, error: (err) => {
            console.log("Failed to Upload Log File");
            console.log(err);
            resolve(false);
          }
        });
    });
  }

  updateUserLanguage(lang: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.put(this.baseUrl + `/user/lang/${lang}`, {})
        .subscribe({
          next: (result: APIResponse<string>) => {
            if (!result.ok) {
              console.log("Failed to Update User's Language");
              console.log(result.data);
              resolve();
            }
            resolve();
          }, error: (err) => {
            console.log("Failed to Update User's Language");
            console.log(err);
            resolve();
          }
        });
    });
  }

  getTermsAndConditions() {
    return this.http.get<any>(this.baseUrl + '/terms-and-conditions', {observe: 'response'});
  }

  setEtag(entity: EtagEntitiesEnum, value: string){
    let currentEtag = JSON.parse(window.localStorage.getItem(CacheNamesEnum.ETAG) || '{"etagValues":{}}') as EtagCacheModel;

    currentEtag.etagValues ??= {} as Record<EtagEntitiesEnum, string>;
    currentEtag.etagValues[entity] = value;
    window.localStorage.setItem(CacheNamesEnum.ETAG, JSON.stringify(currentEtag));
  }

  getEtagValue(entity: EtagEntitiesEnum){
    let currentEtag = JSON.parse(window.localStorage.getItem(CacheNamesEnum.ETAG) || '{"etagValues":{}}') as EtagCacheModel;
    return currentEtag.etagValues?.[entity] ?? null;
  }
}
